import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { useLead } from "@/context/LeadContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const { openLead } = useLead();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`transition-colors duration-500 ${
          scrolled ? "bg-bone/80 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="font-serif text-2xl md:text-3xl tracking-tight text-ink">
            Chateaux
          </Link>

          <div className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-ink/70 hover:text-ink transition-colors duration-300 relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-coral transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => openLead({ source: "nav" })}
              data-testid="nav-cta-trial"
              className="group inline-flex items-center gap-2 bg-coral text-bone px-5 py-2.5 text-sm font-medium hover:bg-ink transition-colors duration-300"
            >
              Start free
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>

          <button
            className="md:hidden text-ink p-2"
            onClick={() => setOpenMenu((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {openMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {openMenu && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden bg-bone border-b border-ink/10 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpenMenu(false)}
                  className="text-lg text-ink/80"
                >
                  {l.label}
                </Link>
              ))}
              <button
                onClick={() => {
                  setOpenMenu(false);
                  openLead({ source: "nav-mobile" });
                }}
                className="bg-coral text-bone px-5 py-3 text-sm font-medium text-left"
                data-testid="nav-mobile-cta"
              >
                Start free
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
